import Vue from "vue";
import suncalc from "suncalc";
import { getEventEditChannel } from "@/utils/eventEditChannel";

const state = {
	dateInterval: null,
	selectedEvent: null,
	expandedDay: null,
	currentView: null,
	filters: {
		product: true,
		resource: true,
		transport: true,
		availability: true,
		//booked: true,
	},
	eventNotes: [],
	selectedNote: null,
	storedDates: {},
	storedEvents: [],
	isloadingEvents: false,
	isloadingSelectedEvent: false,
	isCreatingSingleEvent: false,
	isUpdatingEvent: false,
	filterBlacklistProducts: [],
	selectedEventUpdateFlags: {},
	sunTimes: null,
	sunEvents: null,
	updateEventCancelTokenSource: null,
	updateEventCurrentRequestTimestamp: null,
	isUpdatingEventNote: false,
	basePrices: [],
	basePricesOrignal: [],
	sellGenderPrices: [],
	sendCancelationEmail: false,
	downloadEventsAxiosCancel: null,
};

const getters = {
	getDateInterval: (state) => state.dateInterval,
	getSelectedEvent: (state) => state.selectedEvent,
	getExpandedDay: (state) => state.expandedDay,
	getCurrentView: (state) => state.currentView,
	getFilters: (state) => state.filters,
	getSelectedNote: (state) => state.selectedNote,
	getEventNotes: (state) => state.eventNotes,
	getStoredDates: (state) => state.storedDates,
	getBasePrices: (state) => state.basePrices,
	getStoredEvents: ({ storedDates, dateInterval }) => {
		if (dateInterval && dateInterval.startDate) {
			const temp = [];
			const startDate = moment(dateInterval.startDate);
			const endDate = moment(dateInterval.endDate);
			
			if (storedDates && dateInterval.startDate in storedDates) {
				for (let date = startDate; date.isSameOrBefore(endDate); date.add(1, "days")) {
					const formattedDate = date.format("YYYY-MM-DD");
					if (storedDates[formattedDate]) {
						// We need to show events where the start date is before the start date filter
						// and the end date falls within the filter range.
						// If the event start date is outside the range, the start will be set to midnight of the 'start' property.
						storedDates[formattedDate].events.forEach(element => {
							element.start = element.originalStart;
							
							// Saves original end date to show in calendar
							if (!element.originalEnd) {
							  	element.originalEnd = element.end;
							}
							
							// Calculates the total duration in seconds
							const parts = element.duration.split(':');
							const hours = parseInt(parts[0], 10);
							const mins = parseInt(parts[1], 10);
							const seconds = parseInt(parts[2], 10);
							const totalDurationSeconds = hours * 3600 + mins * 60 + seconds; 
							
							const formatDate = (date) => {
								const year = date.getFullYear();
								const month = String(date.getMonth() + 1).padStart(2, '0');
								const day = String(date.getDate()).padStart(2, '0');
								const hh = String(date.getHours()).padStart(2, '0');
								const mm = String(date.getMinutes()).padStart(2, '0');
								const ss = String(date.getSeconds()).padStart(2, '0');
								
								return `${year}-${month}-${day} ${hh}:${mm}:${ss}`;
							};
							
							// Case 1: If the event has a total duration <= 30 min, ‘stretch’ to 30 min
							if (element.start && totalDurationSeconds <= 1800) {
								const dateIso = element.start.replace(' ', 'T');
								const date = new Date(dateIso);
								
								// Set end to be +30 min from start
								date.setMinutes(date.getMinutes() + 30);
								
								element.end = formatDate(date);
							}
							
							// Case 2: If the event crosses over to the next day
							const startIso = element.originalStart.replace(' ', 'T');
							const startDate = new Date(startIso);
							const endIso = element.end.replace(' ', 'T');
							const endDate = new Date(endIso);
							
							// If start and end are not on the same day, calculates the ‘effective duration’ on the start day
							if (startDate.toDateString() !== endDate.toDateString()) {
								// Set the end of the start day
								const endOfDay = new Date(startDate);
								endOfDay.setHours(24, 0, 0, 0);
								const effectiveDurationSeconds = (endOfDay - startDate) / 1000;
							  
								// If the effective duration is less than 30 min, adjust to guarantee 30 min of display
								if (effectiveDurationSeconds < 1800) {
									// If the event starts after 23:30, we force the start to be 23:30
									if (startDate.getHours() > 23 || (startDate.getHours() === 23 && startDate.getMinutes() >= 30)) {
										startDate.setHours(23, 30, 0, 0);
									}

									element.start = formatDate(startDate);
									element.end = element.originalEnd;
								}
							}
						});

						temp.push(...storedDates[formattedDate].events);
					}
				}
			}

			return temp.filter((event) => event !== undefined);
		}

		return [];
	},
	getIsLoadingEvents: (state) => state.isloadingEvents,
	isCreatingSingleEvent: (state) => state.isCreatingSingleEvent,
	getIsloadingSelectedEvent: (state) => state.isloadingSelectedEvent,
	getIsUpdatingEvent: (state) => state.isUpdatingEvent,
	getfilterBlacklistProducts: (state) => state.filterBlacklistProducts,
	getSelectedEventUpdateFlags: (state) => state.selectedEventUpdateFlags,
	getProductsShowingOnCalendar: (state) => {
		let eventsOnDates = state.storedEvents.map((storedEvent) => {
			if (
				storedEvent &&
				moment(storedEvent.start).isAfter(moment(state.dateInterval.startDate)) || moment(storedEvent.end).isBefore(moment(state.dateInterval.endDate).endOf("day"))
			) {
				return storedEvent;
			}
		});

		let products = [];

		eventsOnDates.forEach((event) => {
			if (!event || !event.product) {
				return false;
			}
			//check if product is already present on products
			let filteredArr = products.filter((product) => {
				return product.id == event.product.id;
			});

			if (filteredArr.length == 0 && event && event.product.name) {
				products.push(event.product);
			}
		});

		return products;
	},
	getIsUpdatingEventNote: (state) => state.isUpdatingEventNote,
};

const actions = {
	eventNotify: ({}, payload) => {
        return axios.get('/api/event/notify', {
            params:{
                emailType: payload.emailType,
                eventId: payload.eventId,
            }
        }).then(response => {
			if (response.status === 200) {
				Vue.toasted.success('Email notifications sent!');
			}
        }).catch(err => {
			Vue.toasted.error('Email notification not sent!');
			Vue.toasted.error('Something went wrong, please try again later!');
		})
    },

	updateSidebar: ({ commit }, date) => {
		commit("UPDATE_CALENDAR_SIDEBAR", date);
	},

	updateExpandedDate: ({ commit }, date) => {
		commit("UPDATE_EXPANDED_DATE", date);
	},

	updateCurrentView: ({ commit }, view) => {
		commit("UPDATE_CURRENT_VIEW", view);
	},

	// Updates the currently selected event on the calendar, triggering
	// its sidebar detailed view. This can only happen after we finish
	// fetching the event notes from the API
	async updateSelectedEvent({ state, commit, dispatch, rootState }, payload) {
		// Cancel previous request
        if (state.updateEventCancelTokenSource) {
            state.updateEventCancelTokenSource.cancel('Operation canceled due to new request.');
        }

        // New cancelToken and timeStamp for new request
        const cancelTokenSource = axios.CancelToken.source();
        const requestTimestamp = Date.now();

        state.updateEventCancelTokenSource = cancelTokenSource;
        state.updateEventCurrentRequestTimestamp = requestTimestamp;
        state.isloadingSelectedEvent = true;

		try {
			if (typeof payload == "number") {
				dispatch("initEventNotes", payload);
				let response = await axios.get("api/calendar/event", {
					params: {
						eventId: payload,
					},
					cancelToken: cancelTokenSource.token
				});

				// Check if is the most recent answer
				if (state.updateEventCurrentRequestTimestamp === requestTimestamp) {
					if (response && response.status === 200 && response.data) {
						commit("UPDATE_SELECTED_EVENT", response.data);
						commit('SET_BASE_PRICES', response.data);
					}
				}
			} else {
				commit("UPDATE_SELECTED_EVENT", payload);
			}
        } catch (err) {
            if (!axios.isCancel(err) && state.updateEventCurrentRequestTimestamp === requestTimestamp) {
                console.log(err);
            }
        } finally {
            // Clear cancelToken and timeStamp
            if (state.updateEventCurrentRequestTimestamp === requestTimestamp) {
				state.isloadingSelectedEvent = false;
                state.updateEventCancelTokenSource = null;
                state.updateEventCurrentRequestTimestamp = null;
            }
        }
	},

	// Toggles a given filter on the calendar

	toggleCalendarFilter: ({ commit }, data) => {
		commit("TOGGLE_CALENDAR_FILTER", data);
	},

	setSelectedNote: ({ state, commit, rootState }, note) => {
		commit("SET_SELECTED_NOTE", note);
	},

	clearSelectedNote: ({ commit }) => {
		commit("CLEAR_SELECTED_NOTE");
	},

	broadcastEventChange: ({ state }, eventId) => {
		const channel = getEventEditChannel();
		if (!channel || !eventId) return;

		const event = state.selectedEvent.id === eventId
			? state.selectedEvent
			: { id: eventId };

		channel.postMessage({ id: eventId, event });
	},

	initEventNotes: ({ commit, dispatch, rootState }, eventId) => {
		axios
			.get("/api/calendar/ListEventNote", {
				params: {
					supplierId: rootState.user.account.supplierId,
					eventId: eventId,
				},
			})
			.then((response) => {
				if (response && response.status === 200) {
					commit("UPDATE_EVENT_NOTE_LIST", response.data);

					Vue.toasted.success(response.data.message);
				}
			})
			.catch(function (error) {
				//Vue.toasted.error(error);
			});
	},

	// Adds an event note (placebo for update method)
	// Parameters: { action: 'edit', fields: { object } }

	addEventNote: ({ dispatch }, data) => {
		dispatch("updateEventNote", data);
		dispatch("closeModal");
	},

	updateEventNote: ({ commit, dispatch, state }, data) => {
		if (!data.action) {
			throw new Error("Invalid action on updateEventNote");
		}

		let action = data.action.charAt(0).toUpperCase() + data.action.slice(1);
		state.isUpdatingEventNote = true;
		axios
			.post("/api/calendar/" + action + "EventNote", data.fields)
			.then((response) => {
				if (response && response.status === 200) {
					dispatch("initEventNotes", response.data.eventId);
					dispatch("clearSelectedNote");
					dispatch("closeModal");
					dispatch("broadcastEventChange", response.data.eventId);

					Vue.toasted.success(response.data.message);
				}
				state.isUpdatingEventNote = false;
			})
			.catch(function (error) {
				state.isUpdatingEventNote = false;
			});
	},

	deleteEventNote: ({ commit, dispatch }, fields) => {
		axios
			.post("/api/calendar/DeleteEventNote", fields)
			.then((response) => {
				if (response && response.status === 200) {
					dispatch("initEventNotes", fields.eventNote.eventId);
					dispatch("clearSelectedNote");
					dispatch("broadcastEventChange", fields.eventNote.eventId);

					Vue.toasted.success(response.data.message);
				}
			})
			.catch(function (error) {
			});
	},

	saveEventChanges: ({ commit, dispatch, state }, data) => {
		commit('SET_IS_UPDATING_EVENT', true);
		return new Promise((resolve, reject) => {
			axios
				.post("/api/calendar/EditEvent", {
					supplierId: data.supplierId,
					event: state.selectedEvent,
					updateFlags: state.selectedEventUpdateFlags,
					notificationFlags: {
						notifyEventCancellation: state.sendCancelationEmail
					}
				})
				.then((response) => {
					commit('SET_IS_UPDATING_EVENT', false);
					Vue.toasted.success("Changes were sucessfully saved.");
					dispatch("closeModal");
					resolve(response);
				})
				.catch(function (error) {
					commit('SET_IS_UPDATING_EVENT', false);
					reject(error);
				});
		});
	},

	setSendCancelationEmail:({ commit }, data) => {
		commit("SET_SEND_CANCELATION_EMAIL", data);
	},

	cancelEventEdit: ({ state, commit }, data) => {
		//swaps the state with a complete event that was saved before any changes
		commit("CANCEL_EVENT_EDIT", data);
	},

	resetBasePrices: ({ state, commit }) => {
		commit("RESET_BASE_PRICES");
	},

	setBasePrices: ({ commit }, event) => {
		commit("SET_BASE_PRICES", event);
	},

	async downloadEvents({ state, commit, dispatch }, data) {
		if (state.downloadEventsAxiosCancel) {
			state.downloadEventsAxiosCancel.cancel('Operation canceled due to new request.');
		}

		const source = axios.CancelToken.source();
		state.downloadEventsAxiosCancel = source;

		let dateFrom = moment(data.dateFrom).format("YYYY-MM-DD");
		let dateTo = moment(data.dateTo).subtract(1, "day").format("YYYY-MM-DD");
		let supplierId = data.supplierId;

		// Update date interval state
		await commit("UPDATE_CALENDAR_DATE_INTERVAL", {
			startDate: dateFrom,
			endDate: dateTo,
		});

		state.isloadingEvents = true;

		return axios.get("api/calendar/GetEvents", {
			params: {
				dateFrom: dateFrom,
				dateTo: dateTo
			},
			cancelToken: source.token
		})
		.then(function ({ status, data: { events } } = { status: 500, data: { events: [] } }) {
			if (status < 200 || status >= 300) return;
			if (!events) return;

			dispatch("flushEvents");
			commit("ADD_TO_STORED_EVENTS_AND_DATES", events);
			dispatch("calculateSunTimes");
		})
		.catch((err) => {
			if (axios.isCancel(err)) throw err;
		})
		.finally(() => {
			state.isloadingEvents = false;
		});
	},

	flushEvents: ({ commit, dispatch, rootState }) => {
		commit("FLUSH_EVENTS");
	},

	setAsCreatingSingleEvent: ({ state }) => {
		state.isCreatingSingleEvent = true;
		state.selectedEvent = {
			id: null,
			availabilityType: 0,
			shareType: 0,
			start: moment().format("YYYY-MM-DD 14:00:00"),
			end: moment().format("YYYY-MM-DD 15:00:00"),
			duration: "01:00:00",
			location: null,
			locationId: null,
			locationMain: null,
			bookingQuestion: null,
			bookingQuestionIsActive: false,
			bookingQuestionIsMandatory: false,
			isPrivateEvent: false,
			manualConfirmation: false,
			notes: "",
			status: 1,
			statusMessage: null,
			hasLotation: true,
			maximumLotation: 0,
			quantityAvailable: 0,
			quantityBooked: 0,
			totalPAXCheckedIn: 0,
			totalPAXNoShow: 0,
			salesPermissionsTeam: true,
			salesPermissionsWebsite: true,
			salesPermissionsAgents: true,
			salesPermissionsAgentsCustom: false,
			salesPermissionsAgentsList: [],
			salesBlockedAgentList: [],
			isPriceIncludeTaxes: true,
			eventGenderPrices: [
				{
					genderPriceType: 1,
					isExcludedFromBilling: false,
					value: 0,
					currency: "€",
					isTaxIncludedInPrice: true,
					vatType: 0,
					productGenderPriceComponents: [],
				},
			],
			eventNotes: [],
			eventAlotments: [],
			eventComponents: [],
			eventOccupancyList: [],
			eventPriceGroups: [],
			product: null,
			eventResources: [],
			eventRoutes: [],
			helperProduct: null,
			channelCommissions: [],
		};
	},

	setAsNotCreatingSingleEvent: ({ state }) => {
		state.isCreatingSingleEvent = false;
	},

	removeEventFromStored: ({ state }, eventId) => {
		state.storedEvents = state.storedEvents.filter((storedEvent) => {
			return storedEvent.id != eventId;
		});
	},

	downloadSingleEvent: ({ state, commit, dispatch, rootState }, eventId) => {
		// receives an eventId, downloads it from API and if it alread exists on storedEvents replaces it,
		// if not present on storedEvents it adds it
		return axios
			.get("api/calendar/event", {
				params: { eventId: eventId },
			})
			.then((response) => {
				if (response.status && response.status == 200) {
					dispatch("removeEventFromStored", eventId);
					
					if (response.data) {
						const freshEvent = response.data;
						state.storedEvents.push(freshEvent);

						commit("REPLACE_EVENT_IN_STORED_DATES", freshEvent);
					}
				}
				return response;
			})
			.catch((err) => {
				return err;
			});
	},
	changefilterBlacklistProducts: function ({ commit }, payload) {
		payload = payload == null ? [] : payload;
		commit("UPDATE_FILTERS_BLACKLIST_PRODUCTS", payload);
	},
	addSelectedEventUpdateFlag: ({ commit }, payload) => {
		commit("ADD_SELECTED_EVENT_UPDATE_FLAG", payload);
	},
	clearUpdateFlags: ({ commit }, payload) => {
		commit("CLEAR_UPDATE_FLAGS", payload);
	},
	calculateSunTimes: ({ commit, state, rootState }) => {
		if (!state.dateInterval || !state.dateInterval.startDate || !state.dateInterval.endDate)
			return;
		//if (!rootState.company.settings.latitude || !rootState.company.settings.longitude) return;
		function formatSunTimes({ sunrise, sunset }) {
			const format = "YYYY-MM-DD HH:mm:ss";
			sunrise = moment(sunrise).format(format);
			sunset = moment(sunset).format(format);

			return [
				{
					id: -999998,
					title: "Sunrise",
					start: sunrise,
					originalStart: sunrise,
					end: sunrise,
					duration: "00:00:01",
					status: null,
					statusDescription: "Active",
					eventType: "sunrise",
					product: {
						id: -999998,
						name: "Sunrise",
						active: true,
						name: "Sunrise",
						code: "SUNRISE",
						eventType: "sunrise",
						color: 1,
					},
					code: "SUNRISE",
					color: 1,
					eventResources: [],
					channelCommissions: [],
					hideOnCalendar:
						rootState.company.settings.isToShowSunriseAndSunsetOnCalendar === false,
				},
				{
					id: -999999,
					title: "Sunset",
					start: sunset,
					originalStart: sunset,
					end: sunset,
					duration: "00:00:01",
					status: null,
					statusDescription: "Active",
					eventType: "sunset",
					product: {
						id: -999999,
						name: "Sunset",
						active: true,
						name: "Sunset",
						code: "SUNSET",
						eventType: "sunrise",
						color: 1,
					},
					code: "SUNSET",
					color: 1,
					eventResources: [],
					channelCommissions: [],
					hideOnCalendar:
						rootState.company.settings.isToShowSunriseAndSunsetOnCalendar === false,
				},
			];
		}

		const latitude = rootState.company.settings.latitude;
		const longitude = rootState.company.settings.longitude;
		const startDate = moment(state.dateInterval.startDate);
		const endDate = moment(state.dateInterval.endDate).add(1, "day");

		const allSunEvents = [];
		for (let date = moment(startDate); date.isBefore(endDate); date.add(1, "days")) {
			const _date = new Date(moment(date).add(12, "hours"));
			const sunTimes = suncalc.getTimes(_date, latitude, longitude);
			const sunEvents = formatSunTimes(sunTimes);
			allSunEvents.push(...sunEvents);
		}

		commit("ADD_TO_STORED_EVENTS_AND_DATES", allSunEvents);
	},

	setBasePricesInEvent: ({ commit, state }) => {
		commit("UPDATE_SELECTED_EVENT_PRICES", state.basePrices);
	},

	setSellPricesInEvent: ({ commit, state }) => {
		commit("UPDATE_SELECTED_EVENT_PRICES", state.sellGenderPrices);
	},
};

const mutations = {
	SET_SEND_CANCELATION_EMAIL: (state, data) => {
		state.sendCancelationEmail = data;
	},

	SET_IS_UPDATING_EVENT: (state, val) => {
		state.isUpdatingEvent = !!val;
	},

	SET_BASE_PRICES: (state, data) => {
		const original = data.eventGenderPricesWithoutMultiplier;
		
		state.basePrices = JSON.parse(JSON.stringify(original));
		state.basePricesOrignal = JSON.parse(JSON.stringify(original));
	},

	RESET_BASE_PRICES: (state) => {
		state.basePrices = JSON.parse(JSON.stringify(state.basePricesOrignal));
	},

	UPDATE_SELECTED_EVENT_PRICES: (state, newPrices) => {
		if (state.selectedEvent.passengerSettings && state.selectedEvent.passengerSettings.format === 2) {
			newPrices.map(gp => {
				gp.tieredConfigurations = state.selectedEvent.passengerSettings.tieredConfigurations[gp.hashedAudience]
			})
		}

		state.selectedEvent.eventGenderPrices = newPrices;
	},

	UPDATE_EXPANDED_DATE: (state, date) => {
		state.expandedDay = date;
	},

	UPDATE_CURRENT_VIEW: (state, view) => {
		state.currentView = view;
	},

	UPDATE_CALENDAR_SIDEBAR: (state, date) => {
		var headers = document.querySelectorAll(".fc-day-header");
		var current = document.querySelector(".fc-day-header[data-date='" + date + "']");

		state.expandedDay = date;
		state.selectedEvent = null;

		// Update UI

		if (headers) {
			[].forEach.call(headers, (i) => i.classList.remove("selected"));
		}

		if (current) {
			current.classList.add("selected");
		}
	},

	UPDATE_SELECTED_EVENT: (state, event) => {
		state.selectedEvent = event;
		state.sellGenderPrices = (event && event.eventGenderPrices) ? event.eventGenderPrices : [];
		state.isloadingSelectedEvent = false;
	},

	TOGGLE_CALENDAR_FILTER: (state, data) => {
		state.filters[data.filter] = data.value;
	},

	SET_SELECTED_NOTE: (state, note) => {
		state.selectedNote = note;
	},

	CLEAR_SELECTED_NOTE: (state) => {
		state.selectedNote = null;
	},

	UPDATE_SELECTED_NOTE: (state, note) => {
		let index = _.findIndex(state.selectedEvent.notes, { id: note.id });

		if (index !== null) {
			Vue.set(state.selectedEvent.notes, index, note);
		}
	},

	UPDATE_EVENT_NOTE_LIST: (state, notes) => {
		state.eventNotes = notes;
	},

	CANCEL_EVENT_EDIT: (state, oldEvent) => {
		state.selectedEvent = oldEvent;
	},

	ADD_TO_STORED_EVENTS_AND_DATES: (state, events) => {
		let storeEvents = [];
		const eventsPerDay = events.reduce((acc, event) => {
				const currentEventDay = event.start.split(" ")[0];

				if (!acc[currentEventDay]) {
					acc[currentEventDay] = {
						date: currentEventDay,
						pullTime: moment(),
						events: [],
					};
				}

				const isSunriseOrSunset =
					event.eventType === "sunrise" || event.eventType === "sunset";
				const isEventExists =
					acc[currentEventDay] &&
					acc[currentEventDay].events.some((e) => e.id === event.id);

				if (!isEventExists) {
					if (isSunriseOrSunset && event.isHidden) {
						return acc;
					}
					acc[currentEventDay].events.push(event);
				} else {
					if (isSunriseOrSunset && event.isHidden) {
						acc[currentEventDay].events = acc[currentEventDay].events.filter(
							(e) => e.id !== event.id
						);
					} else {
						acc[currentEventDay].events = acc[currentEventDay].events.map((e) => {
							if (e.id === event.id) {
								return event;
							}
							return e;
						});
					}
				}

				storeEvents = [ ...acc[currentEventDay].events, ...storeEvents ]

				return acc;
			},
			{ ...state.storedDates }
		);

		state.storedDates = { ...eventsPerDay };
		state.storedEvents = storeEvents;
	},

	FLUSH_EVENTS: (state) => {
		state.storedDates = {};
		state.storedEvents = [];
	},
	REPLACE_EVENT_IN_STORED_DATES: (state, event) => {
		const updatedDates = {};

		Object.keys(state.storedDates).forEach((date) => {
			updatedDates[date] = {
				...state.storedDates[date],
				events: state.storedDates[date].events.filter((e) => e.id !== event.id),
			};
		});

		const eventDate = event.start.split(" ")[0];
		const existingBucket = updatedDates[eventDate] || { date: eventDate, pullTime: moment(), events: [] };

		updatedDates[eventDate] = {
			...existingBucket,
			events: [...existingBucket.events, event],
		};

		state.storedDates = updatedDates;
	},
	UPDATE_FILTERS_BLACKLIST_PRODUCTS: (state, newProdArray) => {
		state.filterBlacklistProducts = newProdArray;
	},

	UPDATE_CALENDAR_DATE_INTERVAL: (state, interval) => {
		state.dateInterval = interval;
	},

	ADD_SELECTED_EVENT_UPDATE_FLAG: (state, flagName) => {
		state.selectedEventUpdateFlags[flagName] = true;
	},

	CLEAR_UPDATE_FLAGS: (state) => {
		state.selectedEventUpdateFlags = {};
	}
};

export default {
	state,
	getters,
	actions,
	mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/calendar.js